import { usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import React from "react"

import { AuthModal } from "@/components/auth-modal"
import { Toaster } from "@/components/ui/sonner"
import { WelcomeModal } from "@/components/welcome-modal"
import { AuthModalProvider, useAuthModal } from "@/hooks/use-auth-modal"
import { FavoritesProvider } from "@/hooks/use-favorites"
import { useFlash } from "@/hooks/use-flash"
import { NotificationProvider } from "@/hooks/use-notifications"

interface PersistentLayoutProps {
  children: ReactNode
}

function AuthModalRenderer() {
  const { isOpen, defaultTab, closeModal } = useAuthModal()
  return (
    <AuthModal isOpen={isOpen} onClose={closeModal} defaultTab={defaultTab} />
  )
}

interface PageProps {
  auth?: {
    user?: {
      id?: number
      onboarding_completed_at?: string | null
      onboarding_dismissed_at?: string | null
    } | null
    is_authenticated?: boolean
  }
  app?: {
    neighborhoods?: { id: number; name: string }[]
    popular_neighborhoods?: { id: number; name: string }[]
    interests?: { id: number; name: string }[]
    [key: string]: unknown
  }
  [key: string]: unknown
}

function WelcomeModalRenderer() {
  const [isOpen, setIsOpen] = React.useState(false)
  const page = usePage()

  const pageProps = page.props as PageProps
  const isAuthenticated = !!pageProps.auth?.is_authenticated
  const onboardingCompletedAt = pageProps.auth?.user?.onboarding_completed_at
  const onboardingDismissedAt = pageProps.auth?.user?.onboarding_dismissed_at
  const needsOnboarding =
    isAuthenticated && !onboardingCompletedAt && !onboardingDismissedAt
  const neighborhoods = pageProps.app?.neighborhoods ?? []
  const popularNeighborhoods = pageProps.app?.popular_neighborhoods ?? []
  const interests = pageProps.app?.interests ?? []

  React.useEffect(() => {
    if (!isAuthenticated || !needsOnboarding) {
      setIsOpen(false)
      return
    }

    setIsOpen(true)
  }, [isAuthenticated, needsOnboarding])

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <WelcomeModal
      isOpen={isOpen}
      onClose={handleClose}
      neighborhoods={neighborhoods}
      popularNeighborhoods={popularNeighborhoods}
      interests={interests}
    />
  )
}

export default function PersistentLayout({ children }: PersistentLayoutProps) {
  useFlash()
  return (
    <FavoritesProvider>
      <NotificationProvider>
        <AuthModalProvider>
          {children}
          <WelcomeModalRenderer />
          <AuthModalRenderer />
          <Toaster richColors />
        </AuthModalProvider>
      </NotificationProvider>
    </FavoritesProvider>
  )
}
